/**
 * Product Card Period Selector
 * Handles period selection for products using metafield variants
 */

class ProductCardPeriodSelector {
  constructor() {
    this.init();
  }

  init() {
    // Aguardar o DOM estar pronto
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    // Usar delegação de eventos para lidar com cards carregados dinamicamente
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.product-card__period-button[data-metafield-variant="true"]');
      if (button) {
        e.preventDefault();
        this.handlePeriodSelection(button);
      }
    });
    
    // Inicializar quantidade do carrinho para todos os cards existentes
    this.initializeCartQuantities();
  }
  
  initializeCartQuantities() {
    // Para cada card com period selector, configurar a quantidade inicial
    const selectors = document.querySelectorAll('.product-card__period-selector');
    selectors.forEach(selector => {
      const activeButton = selector.querySelector('.product-card__period-button.active[data-metafield-variant="true"]');
      if (activeButton) {
        const productCard = activeButton.closest('.product-card, .card-wrapper, .product-card--modern, .product-card__media-wrapper');
        const cartQuantity = parseInt(activeButton.dataset.cartQuantity) || parseInt(activeButton.dataset.periodMultiplier) || 1;
        if (productCard) {
          this.updateCartQuantity(productCard, cartQuantity);
        }
      }
    });
  }

  handlePeriodSelection(button) {
    const selector = button.closest('.product-card__period-selector');
    const productCard = button.closest('.product-card, .card-wrapper, .product-card--modern, .product-card__media-wrapper');
    if (!selector || !productCard) return;

    // Remover classe active de todos os botões do mesmo seletor
    selector.querySelectorAll('.product-card__period-button').forEach(btn => {
      btn.classList.remove('active');
    });

    // Adicionar classe active ao botão clicado
    button.classList.add('active');

    // Obter dados do botão
    const multiplier = parseInt(button.dataset.periodMultiplier) || 1;
    const cartQuantity = parseInt(button.dataset.cartQuantity) || multiplier;
    const basePrice = parseInt(button.dataset.basePrice) || 0;
    const discountData = JSON.parse(button.dataset.baseDiscountSecondMonth);
    
    // Obter desconto baseado na opção selecionada
    const discountFirstMonth = parseFloat(discountData.desconto_opcao_1) || 0;
    const discountSecondMonth = parseFloat(discountData.desconto_opcao_2) || 0;
   
    // Calcular novo preço com desconto aplicado em ambas as opções
    const totalPrice = basePrice * multiplier;
    const discountPercentage = multiplier === 1 ? discountFirstMonth : discountSecondMonth;
    const discountAmount = totalPrice * (discountPercentage / 100);
    const newPrice = totalPrice - discountAmount;
    // Preço comparativo: sempre mostra o preço total sem desconto quando há desconto
    const newComparePrice = discountPercentage > 0 ? totalPrice : 0;
    
    // Atualizar preço no card
    this.updateCardPrice(productCard, newPrice, basePrice, multiplier);
    this.updateComparePrice(productCard, newComparePrice);
    this.updateDiscountTag(productCard, discountPercentage);
    
    // Atualizar quantidade do carrinho no formulário do card
    this.updateCartQuantity(productCard, cartQuantity);
  }
  
  updateCartQuantity(productCard, cartQuantity) {
    // Encontrar o formulário de add-to-cart no card
    const form = productCard.querySelector('form.product-card__add-to-cart--form');
    if (form) {
      // Encontrar ou criar input de quantidade
      let quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = cartQuantity;
      } else {
        // Criar input hidden se não existir
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = 'quantity';
        hiddenInput.value = cartQuantity;
        form.appendChild(hiddenInput);
      }
    }
    
    // Armazenar no card para uso posterior
    productCard.dataset.currentCartQuantity = cartQuantity;
  }

  updateCardPrice(productCard, newPrice, basePrice, multiplier) {
    // Encontrar elementos de preço no card
    const priceElements = productCard.querySelectorAll('.price__regular .money, .price .money, .product-card__price .money, .product-card__price-current');
    priceElements.forEach(priceElement => {
      // Verificar se não é um preço riscado (compare_at_price)
      const isComparePrice = priceElement.closest('.price__compare, .product-card__price-compare') || 
                            priceElement.parentElement.classList.contains('price__compare, .product-card__price-compare');
      
      if (!isComparePrice) {
        // Formatar o novo preço usando a formatação do Shopify
        const formattedPrice = this.formatMoney(newPrice);
        priceElement.textContent = formattedPrice;
      } 
    });

    // Atualizar data attributes se necessário
    const priceContainer = productCard.querySelector('.price, .product-card__price, .product-card__price-current');
    if (priceContainer) {
      priceContainer.dataset.currentPrice = newPrice;
      priceContainer.dataset.multiplier = multiplier;
    }

    // Disparar evento customizado para outros scripts que possam precisar
    productCard.dispatchEvent(new CustomEvent('priceUpdated', {
      detail: {
        newPrice: newPrice,
        basePrice: basePrice,
        multiplier: multiplier
      }
    }));
  }
      
  updateComparePrice(productCard, newComparePrice) {
    const comparePriceElement = productCard.querySelector('.product-card__price-compare');
    if (comparePriceElement) {
      if (newComparePrice > 0) {
        comparePriceElement.textContent = this.formatMoney(newComparePrice);
        comparePriceElement.style.display = '';
      } else {
        comparePriceElement.style.display = 'none';
      }
    }
  }

  updateDiscountTag(productCard, discountPercentage) {
    const discountTag = productCard.querySelector('[data-period-discount-tag]');
    if (!discountTag) return;

    if (discountPercentage > 0) {
      discountTag.textContent = `${Math.round(discountPercentage)}%`;
      discountTag.style.display = 'inline-block';
    } else {
      discountTag.style.display = 'none';
    }
  }

  formatMoney(cents) {
    // Usar a formatação de moeda do Shopify se disponível
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    // Fallback para formatação básica em Real brasileiro
    const reais = (cents / 100).toFixed(2);
    return `R$ ${reais.replace('.', ',')}`;
  }
}

// Inicializar quando o script carregar
new ProductCardPeriodSelector();

// Exportar para uso em outros scripts se necessário
window.ProductCardPeriodSelector = ProductCardPeriodSelector;
